// src/hooks/useOperatorApi.tsx

import { Auth, Storage } from 'aws-amplify'
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'

import { FolderId, UserId } from '@/types/domain'

import {
  authHeader,
  setStorage,
  removeStorage,
  notifyError,
  notifySuccess,
} from '../api/'
import { languageConstants as LC } from '../constants/'

import { refreshToken } from './auth/'

const { REACT_APP_API_URL } = import.meta.env

const setLastActivity = () =>
  localStorage.setItem('KARMAlastActivity', Date.now().toString())

export type ViewOption = {
  folder_id: FolderId
  columns?: string
}

export type OperatorData = {
  id: UserId
  isAdmin: boolean
  contracts: number[]
  folders: number[]
  sensors: number[]
  devices: unknown[]
  reports: unknown[]
  view_options: ViewOption[]
  image_url?: string
  first_name?: string
  last_name?: string
  email?: string
  [key: string]: any
  dashboard_is_active: boolean
}

type OperatorContextValue = {
  data: OperatorData | null
  refresh?: () => Promise<void>
}

const OperatorCtx = createContext<OperatorContextValue>({
  data: null,
})

type Props = {
  children: ReactNode
}

export const OperatorDataProvider = ({ children }: Props) => (
  <OperatorCtx.Provider value={useProvideOperator()}>
    {children}
  </OperatorCtx.Provider>
)

const useProvideOperator = () => {
  const [data, setData] = useState<OperatorData | null>(null)

  const loadOperator = useCallback(async () => {
    try {
      await refreshToken()
      const url = `${REACT_APP_API_URL}/user`
      const options = { method: 'GET', headers: { ...authHeader() } }
      const res = await fetch(url, options)
      if (!res.ok) {
        const text = await res.text()
        throw {
          message: text || `HTTP ${res.status}`,
          status: res.status,
        }
      }

      const result = await res.json()
      const node = result?.data || {}

      const attributes = node?.attributes || {}
      const id = Number(node?.id) || null
      const contracts = node?.contracts?.data
        ? node.contracts.data.map(c => Number(c.id))
        : []
      const devices = Array.isArray(node?.devices)
        ? node.devices.map(d => d.attributes)
        : []
      const folders = node?.folders?.data
        ? node.folders.data.map(f => Number(f.id))
        : []
      const reports = Array.isArray(node?.reports)
        ? node.reports.map(r => r.attributes)
        : []
      const sensors = node?.sensors?.data
        ? node.sensors.data.map(s => Number(s.id))
        : []
      const view_options = node?.view_options ?? []

      const isAdmin = attributes?.role_id === 1 || attributes?.role_id === 3

      setData({
        ...attributes,
        contracts,
        devices,
        folders,
        reports,
        sensors,
        id,
        isAdmin,
        view_options,
      })
    } catch (error) {
      setData(null)
      const status = error?.status ?? error?.response?.status
      notifyError(
        `operator error${status ? ` (${status})` : ''}: ${error?.message}`
      )
    }
  }, [])

  useEffect(() => {
    ;(async () => {
      await loadOperator()
    })()
  }, [loadOperator])

  return { data, refresh: loadOperator }
}

export const useOperator = () => useContext(OperatorCtx).data

export const useOperatorApi = () => {
  const navigate = useNavigate()
  const operatorCtx = useContext(OperatorCtx)
  const opData = operatorCtx?.data

  const withRequest = useCallback(async fn => {
    try {
      await refreshToken()
      return await fn()
    } catch (error) {
      const message = error?.message
      notifyError(message)
      return null
    }
  }, [])

  const uploadAws = useCallback(async (file, dirName) => {
    const handle = new File(
      [file],
      (
        Math.random().toString(36).substring(2, 16) +
        Math.random().toString(36).substring(2, 16)
      ).toUpperCase() +
        Date.now() +
        file.name.slice(file.name.lastIndexOf('.'))
    )

    const key = `${dirName}/${handle.name}`

    return (await Storage.put(key, handle, {
      contentType: handle.type,
    })) as unknown as { location: string }
  }, [])

  const update = useCallback(
    (id: UserId, user) =>
      withRequest(async () => {
        let attributes = { ...user }
        if (attributes.image_url instanceof Object) {
          const upload = await uploadAws(attributes.image_url, 'users')
          attributes = { ...attributes, image_url: upload.location }
        }

        const view_options = attributes.view_options
        delete attributes.view_options

        const body = {
          data: { type: 'user', id, attributes, view_options },
        }

        const url = `${REACT_APP_API_URL}/user/${id}`
        const options = {
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          method: 'PATCH',
          body: JSON.stringify(body),
        }
        const res = await fetch(url, options)
        if (!res.ok) {
          const text = await res.text()
          throw {
            message: text || `HTTP ${res.status}`,
            status: res.status,
          }
        }

        const result = await res.json()
        notifySuccess(LC.USER_UPDATED)
        if (typeof operatorCtx?.refresh === 'function')
          await operatorCtx.refresh()

        return result
      }),
    [withRequest, uploadAws, operatorCtx]
  )

  const updatePassword = useCallback(
    (fromPassword, toPassword) =>
      withRequest(async () => {
        const currentUser = await Auth.currentAuthenticatedUser().catch(
          () => null
        )

        if (!currentUser) throw new Error('Kein angemeldeter Benutzer')

        await Auth.changePassword(currentUser, fromPassword, toPassword)
        notifySuccess(LC.USER_PASSWORD_CHANGED)
        return true
      }),
    [withRequest]
  )

  const getFileUpload = useCallback(
    async (file: File, dirName: string = 'users'): Promise<string | null> => {
      try {
        const handle = new File(
          [file],
          (
            Math.random().toString(36).substring(2, 16) +
            Math.random().toString(36).substring(2, 16)
          ).toUpperCase() +
            Date.now() +
            file.name.slice(file.name.lastIndexOf('.'))
        )

        const key = `${dirName}/${handle.name}`

        const result = await Storage.put(key, handle, {
          contentType: handle.type,
        })

        // Public URL (falls dein Bucket public ist)
        return result?.key ? await Storage.get(result.key) : null
      } catch (error) {
        notifyError(error?.message || 'Upload fehlgeschlagen')
        return null
      }
    },
    []
  )

  const login = useCallback(
    (email, password, onSuccess) =>
      withRequest(async () => {
        const user = await Auth.signIn(email, password)
        const session = user?.signInUserSession || (await Auth.currentSession())
        const access = session.getAccessToken().getJwtToken()
        const idTokenObj = session.getIdToken()
        const idToken = idTokenObj.getJwtToken()
        const payload = idTokenObj?.payload || {}

        const simpleUser = {
          id: payload.sub,
          email: payload.email,
          firstName: payload.name,
          lastName: payload.family_name,
          token: idToken,
          accessToken: access,
          idToken,
        }
        setStorage('user', simpleUser)

        navigate('/')

        try {
          const url = `${REACT_APP_API_URL}/user/admin/users/config`
          const options = { headers: { ...authHeader() }, method: 'GET' }
          const result = await fetch(url, options)

          if (result.ok) {
            const cfg = await result.json()
            const normalized = {
              s3: {
                bucketName: cfg?.S3_BUCKET_NAME || null,
                region: cfg?.S3_REGION || null,
                accessKeyId: cfg?.S3_ACCESSKEYID || null,
                secretAccessKey: cfg?.S3_SECRETACCESSKEY || null,
              },
            }
            setStorage('config', normalized)
          } else {
            console.error(
              'Config fetch failed:',
              result.status,
              result.statusText
            )
          }
        } catch (error) {
          console.error('Config fetch error:', error)
        }

        if (typeof onSuccess === 'function') onSuccess(user)
        return { ok: true, user: simpleUser }
      }).catch(error => {
        const code = error?.code
        const message = error?.message || 'Login fehlgeschlagen'
        if (code === 'UserNotConfirmedException')
          setStorage('errorNotConfirmed', true)

        return { ok: false, error: message, code }
      }),
    [withRequest, navigate]
  )

  const logout = useCallback(
    (redirectTo = '/login-page') =>
      withRequest(async () => {
        removeStorage('user')
        removeStorage('config')
        removeStorage('errorNotConfirmed')
        try {
          await Auth.signOut()
        } catch (error) {
          console.error('Amplify signOut:', error)
        }
        navigate(redirectTo)
        return { ok: true }
      }).catch(error => {
        const message = error?.message || 'Logout fehlgeschlagen'
        console.error('Logout error:', error)
        return { ok: false, error: message }
      }),
    [withRequest, navigate]
  )

  const getViewOptions = useCallback(
    (id: FolderId) => {
      const defaultColumns = {
        date: true,
        actual_consumption: true,
        trend: true,
        emissions: true,
        area_consumption: true,
        employee_consumption: true,
        actual_costs: true,
        area_costs: true,
        benchmark: true,
        counter_reading: true,
        employee_costs: true,
      }

      if (!opData)
        return { columns: defaultColumns, header: false, values: true }

      const { view_options = [] } = opData

      const voAll = view_options.find(
        f => f.folder_id === null || f.folder_id === 0
      )
      const voCurrent = view_options.find(f => f.folder_id === id)

      const columns = { ...defaultColumns }
      let header = false
      let values = true

      const applyVO = vo => {
        if (!vo || !vo.columns) return

        const [ucStr = '', colsStr = ''] = String(vo.columns).split('x')

        const colBits = colsStr.split('').map(n => Boolean(Number(n)))
        const keys = Object.keys(columns)
        keys.forEach((key, idx) => {
          if (idx < colBits.length) columns[key] = colBits[idx]
        })

        const ucBits = ucStr.split('').map(n => Boolean(Number(n)))
        if (typeof ucBits[0] === 'boolean') header = ucBits[0]
        if (typeof ucBits[1] === 'boolean') values = ucBits[1]
      }

      applyVO(voAll)
      applyVO(voCurrent)

      return { columns, header, values }
    },
    [opData]
  )

  const getIdleLogout = useCallback((onLogout, timeout = 300000) => {
    if (typeof onLogout !== 'function')
      throw new Error('onLogout muss eine Funktion sein')

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart']
    let timerId

    const checkIdleTime = () => {
      const lastActivity = parseInt(
        localStorage.getItem('KARMAlastActivity'),
        10
      )
      const now = Date.now()
      if (Number.isFinite(lastActivity) && now - lastActivity > timeout)
        onLogout()
    }

    const resetTimer = () => {
      clearTimeout(timerId)
      setLastActivity()
      timerId = setTimeout(checkIdleTime, timeout)
    }

    setLastActivity()
    checkIdleTime()
    resetTimer()

    events.forEach(event => window.addEventListener(event, resetTimer))

    return () => {
      clearTimeout(timerId)
      events.forEach(event => window.removeEventListener(event, resetTimer))
    }
  }, [])

  return {
    getFileUpload,
    getIdleLogout,
    getViewOptions,
    login,
    logout,
    update,
    updatePassword,
  }
}
